import Vue from 'vue'
import { mapActions, mapMutations, mapState } from 'vuex'
import archivableMixin from './archivableMixin'
import jobPartiesMixin from './jobPartiesMixin'
import statusesMixin from './statusesMixin'
import waitForRefreshApplicationMixin from './waitForRefreshApplicationMixin'
import { getJob } from '@/api/jobs/job/job'
import { JobOfferStages } from '@/constants/job/jobOfferStages'
import snackMixin from '@/mixins/snackMixin'
import Job from '@/models-ts/job/Job'
import { RootState } from '@/store'
import { isTronAddress } from '@/utils-ts/tron'

export default Vue.extend({
  mixins: [archivableMixin, jobPartiesMixin, snackMixin, statusesMixin, waitForRefreshApplicationMixin],
  computed: {
    ...mapState<RootState>({
      application: (state: RootState) => state.jobChat.application.value,
      createContractLoading: (state: any) => state.jobDetails.createContractLoading,
    }),
    hasStartJob() {
      return this.isEmployer && !this.isStarted && this.application.offer?.stage === JobOfferStages.ACCEPTED_BY_CUSTOMER
    },
    starting() {
      return this.createContractLoading[this.application?.job?.id]
    },
  },
  methods: {
    ...mapMutations({
      setCreateContractLoading: 'jobDetails/setCreateContractLoading',
      setDepositingOffer: 'jobChat/setDepositingOffer',
    }),
    ...mapActions({
      signCreateContract: 'jobDetails/signCreateContractV2',
      openModal: 'ui/openModal',
    }),
    async startJob() {
      try {
        this.setCreateContractLoading({ jobId: this.application?.job?.id })
        this.setDepositingOffer(this.application.offer?.id)
        if (this.isMetaArchived) {
          await this.setArchivedMeta(false)
        }

        let job
        try {
          job = Job.fromServer(await getJob(`${this.application.job.slug}-${this.application.job.id}`))
        } catch (err) {
          this.openSnackbar({
            type: this.SnackTypes.FAILURE,
            text: `Error fetching job details. Please try again.`,
          })
          this.setCreateContractLoading({ jobId: this.application?.job?.id, loading: false })
          this.setDepositingOffer(null)

          throw err
        }

        if (job) {
          const offer = this.application.offer

          if (isTronAddress(offer.freelancerWallet)) {
            this.openModal({
              component: 'lx-lazy-modal',
              props: {
                factory: import(/* webpackChunkName: "welcome-modal" */ '@/modals/TronIsNoLongerSupported/TronIsNoLongerSupported.vue'),
              },
            })
            this.setCreateContractLoading({ jobId: this.application?.job?.id, loading: false })
            this.setDepositingOffer(null)
            return
          }

          this.signCreateContract({
            params: {
              deadline: offer.deadline,
              rate: offer.budget,
              id: job.id,
              slug: job.slug,
              scId: job.sc_id,
              name: job.name,
              offerId: offer.id,
              freelancer: {
                ...job.freelancer,
                wallet: offer.freelancerWallet,
              },
            },
            successSign: async () => {
              this.openSnackbar({
                type: this.SnackTypes.SUCCESS,
                text: 'Your funds have successfully been deposited to escrow.',
              })
              this.setCreateContractLoading({ jobId: this.application?.job?.id, loading: false })
              this.setDepositingOffer(null)

              await this.waitRefreshApplication()
            },
            cancelSign: () => {
              this.setCreateContractLoading({ jobId: this.application?.job?.id, loading: false })
              this.setDepositingOffer(null)
            },
          })
        }
      } catch (err) {
        console.error(err)
        this.openSnackbar({
          type: this.SnackTypes.FAILURE,
          text: 'Error starting job',
        })
        this.setCreateContractLoading({ jobId: this.application?.job?.id, loading: false })
        this.setDepositingOffer(null)
      }
    },
  },
})
